// -----------------------------------------------------------------------------
// <copyright file="login.guards.ts" company="ecoInsight, Inc.">
// Copyright © ecoInsight, Inc. All rights reserved.
// </copyright>
// ------------------------------------------------------------------------------

import { inject, Injectable } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { firstValueFrom, Observable, of, from, BehaviorSubject } from 'rxjs';
import { filter, switchMap, take, catchError, map } from 'rxjs/operators';
import { LoginService } from '../services/login.service';
import { GlobalService } from '../../shared/services/global.service';
import { environment } from '../../../environments/environment';
import { ModalService } from '../../shared/modals/modal.service';
import { IframeAuthService } from '../services/iframe-auth'; // adjust path if needed
import { Router } from '@angular/router';

@Injectable()
export class LoginGuard {
  private readonly msalService = inject(MsalService);
  private readonly msalBroadcastService = inject(MsalBroadcastService);
  private readonly globalService = inject(GlobalService);
  private readonly loginService = inject(LoginService);
  private readonly modalService = inject(ModalService);
  private readonly iframeAuth = inject(IframeAuthService); // injected
  private readonly router = inject(Router);

  private readonly initializationSubject = new BehaviorSubject<boolean | null>(null);
  private isInitializing = false;

  private readonly isIframe = (() => {
    try {
      return window.self !== window.top;
    } catch {
      return true;
    }
  })();

  // Only true for genuine MSAL auth popups that carry an auth code/error in the URL.
  // window.open() from an external app (e.g. CPQ) also sets window.opener but has no
  // MSAL auth params — those windows must go through the normal VAS auth flow.
  private readonly isMsalAuthPopup = (() => {
    if (!window.opener || window.opener === window) return false;
    const search = new URLSearchParams(window.location.search);
    const hash = new URLSearchParams(window.location.hash.substring(1));
    return search.has('code') || search.has('error') || hash.has('code') || hash.has('error');
  })();

  canActivate(): Observable<boolean> | boolean {
    // Genuine MSAL auth popup mid-flow — let MSAL handle the redirect and close the window
    if (this.isMsalAuthPopup) {
      return true;
    }

    // Inside iframe — bypass MSAL broadcast wait, use host token flow
    if (this.isIframe) {
      return this.handleIframeInitialization();
    }

    return this.msalBroadcastService.inProgress$.pipe(
      filter((status: InteractionStatus) => status === InteractionStatus.None),
      take(1),
      switchMap(() => this.handleInitialization()),
    );
  }

  // IFRAME INITIALIZATION — uses token from host, no MSAL interaction
  private handleIframeInitialization(): Observable<boolean> {
    // Restore an existing app session if present
    const sessionData = this.getStoredSessionData();
    if (sessionData) {
      this.restoreSession(sessionData);
      return of(true);
    }

    // Need a host token to proceed
    const hostToken = this.iframeAuth.getToken();
    if (!hostToken) {
      // No token yet — host hasn't sent it (or silent auth not done)
      this.globalService.appInitialized.set(true);
      return of(false);
    }

    // Build app session from the host token
    return this.performIframeLogin(hostToken);
  }

  private performIframeLogin(hostToken: string): Observable<boolean> {
    this.isInitializing = true;

    return from(
      firstValueFrom(
        this.loginService.getBusinessUnits().pipe(
          switchMap((result) => {            
            this.storeUserToken(hostToken);
            this.setUserPref({
              Token: hostToken,
            });
            this.globalService.setDataToStorage('business-units', result.Result);
            if(result.Result.length > 1) {
              localStorage.removeItem('selected-business-unit');
              return of({success: true})
            }
            return this.loginService.fetchAndCacheLookupData();
          }),
          switchMap(() => {
            this.loginService.navigateToProjectList();
            return of(true);
          }),
          catchError((err) => {
            if (err?.Code === '409') {
              this.handleConflictError(err?.Message);
            }
            this.completeInitialization(false);
            return of(false);
          }),
        ),
      )
        .then((result) => {
          this.completeInitialization(result);
          return result;
        })
        .catch(() => {
          this.completeInitialization(false);
          return false;
        }),
    );
  }

  // STANDALONE INITIALIZATION
  private handleInitialization(): Observable<boolean> {
    if (this.msalService.instance.getAllAccounts().length <= 0) {
      this.globalService.appInitialized.set(true);
      return of(true);
    }

    const sessionData = this.getStoredSessionData();
    if (sessionData) {
      this.restoreSession(sessionData);
      this.loginService.navigateToProjectList();
      return of(false);
    }

    if (!this.globalService.appInitialized()) {
      if (this.isInitializing) {
        return this.initializationSubject.asObservable().pipe(
          filter((val) => val !== null),
          map((val) => val as boolean),
          take(1),
        );
      }
      return this.performLogin();
    }

    return of(false);
  }

  private getStoredSessionData(): { userData: any; token: string } | null {
    const userDetails =
      localStorage.getItem('app_user_details') || sessionStorage.getItem('app_user_details');
    const token = localStorage.getItem('app_token') || sessionStorage.getItem('app_token');

    if (userDetails && token) {
      return { userData: JSON.parse(userDetails), token };
    }
    return null;
  }

  private restoreSession(sessionData: { userData: any; token: string }): void {
    sessionStorage.setItem('app_user_details', JSON.stringify(sessionData.userData));
    sessionStorage.setItem('app_token', sessionData.token);
    this.globalService.setCurrency(JSON.parse(sessionData.userData?.CurrencyJson).currencySymbols);
    this.globalService.setToken(sessionData.token);
    if(this.globalService.isBusinessUnitSelected()){
      this.loginService.fetchAndCacheLookupData().subscribe();
    }

    setTimeout(() => {
      this.globalService.appInitialized.set(true);
    });
  }

  private performLogin(): Observable<boolean> {
    this.isInitializing = true;
    const request = { scopes: environment.apiConfig.scopes };

    return from(
      firstValueFrom(this.msalService.acquireTokenSilent(request))
        .then((tokenResponse) => {
          if (tokenResponse) {
            return this.handleTokenResponse(tokenResponse);
          }
          this.completeInitialization(false);
          return false;
        })
        .catch(() => {
          this.completeInitialization(false);

          // Only redirect in standalone window — never in iframe or popup
          if (!this.isIframe && !this.isMsalAuthPopup) {
            this.msalService.loginRedirect({
              scopes: environment.apiConfig.scopes,
            });
          }

          return false;
        }),
    );
  }

  private handleTokenResponse(tokenResponse: any): Promise<boolean> {
    return firstValueFrom(
      this.loginService.getBusinessUnits().pipe(
        switchMap((res) => {
          this.globalService.setDataToStorage('business-units', res.Result);
          this.storeUserToken(tokenResponse.accessToken);
          this.msalService.instance.setActiveAccount(tokenResponse.account);
          this.setUserPref({
            Token: tokenResponse.accessToken,
          });
          if(res.Result.length > 1) {
            localStorage.removeItem('selected-business-unit');
            return of({success: true})
          }
          return this.loginService.fetchAndCacheLookupData();
        }),
        switchMap(() => {
          // Only navigate in standalone or iframe — not in popup
          if (!this.isMsalAuthPopup) {
            this.loginService.navigateToProjectList();
          }
          return of(true);
        }),
        catchError((err) => {
          if (err?.Code === '409') {
            this.handleConflictError(err?.Message);
          }
          this.completeInitialization(false);
          return of(false);
        }),
      ),
    )
      .then((result) => {
        this.completeInitialization(result);
        return result;
      })
      .catch(() => {
        this.completeInitialization(false);
        return false;
      });
  }

  private storeUserToken(token: string): void {
    localStorage.setItem('app_token', token);
    sessionStorage.setItem('app_token', token);
  }  

  private completeInitialization(success: boolean): void {
    this.globalService.appInitialized.set(true);
    this.isInitializing = false;
    this.initializationSubject.next(success);
  }

  private setUserPref(res: any): void {
    this.globalService.setRefreshToken(null);
    this.globalService.setToken(res.Token);
  }

  private handleConflictError(message: string): void {
    const dialog = this.modalService
      .openCustomWarningDialog({
        message,
        icon: 'i-alert-n',
        btnTxt2: 'cmn.ok',
        showIcon: true,
        showCheckBox: false,
        checkBoxMsg: '',
      })
      .subscribe(() => {
        dialog.unsubscribe();
        // Only logout redirect in standalone window
        if (!this.isIframe && !this.isMsalAuthPopup) {
          localStorage.removeItem('selected-business-unit');
          this.msalService.logoutRedirect({});
        }
      });
  }
}
